import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { ActivatedRoute } from '@angular/router';
import { BackButtonService } from '../../back-button.service';
import { CodaltComponent } from '../../codalt.component';
import { CustomerProject } from '../../customer-project.interface';
import { DateNLFormatAdapter } from '../../date-adapters/date-nlformat-adapter';
import { NewsDocument } from '../../models/news-document.interface';
import { TimelineDocument } from '../../models/timeline-document';
import { TimelineImage } from '../../models/timeline-image.interface';
import { Timeline } from '../../models/timeline.interface';
import { Routenames } from '../../route-names.enum';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../../services/customer-project/customer-project.service';
import { PageTitleService } from '../../services/page-title/page-title.service';
import { TimelineService } from '../../services/timeline.service';
import { Utils } from '../../utils.class';
import { handleTimeValueChange } from '../../utils/handle-time-value-change';
import { validTime } from '../../validators/valid-time.validator';

@Component({
    selector: 'app-timeline-edit',
    templateUrl: './timeline-edit.component.html',
    styleUrls: ['../../shared-editor-date-columns.scss'],
    providers: [{ provide: DateAdapter, useClass: DateNLFormatAdapter }],
    standalone: false,
})
export class TimelineEditComponent extends CodaltComponent implements OnInit {
    reserve = false;
    saving = false;
    form: FormGroup;
    timeline: Timeline;
    projects: CustomerProject[];
    projectId: number;
    customerId: number;

    constructor(
        private timelineService: TimelineService,
        private customerProjectService: CustomerProjectService,
        private backButtonService: BackButtonService,
        private dialog: ConfirmDialogService,
        private route: ActivatedRoute,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    reorderImages(event: CdkDragDrop<TimelineImage[]>) {
        const movedImage = this.timeline.images[event.previousIndex];
        this.timeline.images.splice(event.previousIndex, 1);
        this.timeline.images.splice(event.currentIndex, 0, movedImage);
        this.timelineService.sortImages(this.timeline).subscribe();
    }

    imageAdaptChanged() {
        this.timelineService.sortImages(this.timeline).subscribe();
    }

    ngOnInit(): void {
        this.pageTitleService.setTitle(this.getPageTitleLabel());
        this.watchRouteParams(
            [this.RouteParamnames.customerId, this.RouteParamnames.projectId, this.RouteParamnames.timelineId],
            ({ customerId, projectId, timelineId }) => {
                this.projectId = +projectId || undefined;
                this.customerId = +customerId || undefined;
                this.getTimeline(timelineId);
            }
        );
        this.customerProjectService.getProjects(true).subscribe(projects => (this.projects = projects));
    }

    public openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.form.dirty) {
                this.dialog
                    .confirm(
                        'Niet opgeslagen wijzigingen',
                        `Wilt u de niet opgeslagen wijzigingen opslaan of verwerpen?`,
                        'Wijzigingen opslaan',
                        'Wijzigingen verwerpen'
                    )
                    .then(() => {
                        this.save();
                        resolve(false);
                    })
                    .catch(() => resolve(true));
            } else {
                resolve(true);
            }
        });
    }

    save() {
        Utils.triggerValidation(this.form);
        if (this.form.valid) {
            this.saving = true;
            this.timelineService.save(this.form.value).subscribe(
                timeline => {
                    Object.assign(this.timeline, timeline.data);
                    this.form.markAsPristine();
                    this.saving = false;
                    this.backButtonService.navigateBack();
                },
                () => (this.saving = false)
            );
        }
    }

    uploadedFiles(files) {
        if (!this.timeline.images) {
            this.timeline.images = [];
        }
        files.file.forEach(file =>
            this.timelineService.addImage(this.timeline.id, file.filename).subscribe(img => {
                const loading = this.timeline.images.find(p => !p.id);
                if (loading) {
                    img.data.thumb = loading.thumb;
                    img.data.adapt_image = true;
                    Object.assign(loading, img.data);
                } else {
                    this.timeline.images.push(img.data);
                }
            })
        );
    }

    uploadFiles(files?: File[]) {
        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (!this.timeline.images) {
                    this.timeline.images = [];
                }
                this.timeline.images.push({
                    thumb: reader.result.toString(),
                } as TimelineImage);
            };
        });
    }

    deleteImage(image: TimelineImage) {
        this.timelineService.rmImage(image.id).subscribe(() => {
            const index = this.timeline.images.indexOf(image);
            this.timeline.images.splice(index, 1);
        });
    }

    uploadedMainImage(file) {
        this.form.get('image').setValue(file ? file.file.filename : null);
    }

    uploadMainImage(files) {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
            this.timeline.image_thumb = reader.result.toString();
        };
        this.form.get('image').setValue(null);
    }

    reorderDocuments(event: CdkDragDrop<NewsDocument[]>) {
        const movedImage = this.timeline.documents[event.previousIndex];
        this.timeline.documents.splice(event.previousIndex, 1);
        this.timeline.documents.splice(event.currentIndex, 0, movedImage);
        this.timelineService.sortDocuments(this.timeline).subscribe();
    }

    deleteDocument(document: TimelineDocument) {
        this.timelineService.rmDocument(document.id).subscribe(() => {
            const index = this.timeline.documents.indexOf(document);
            this.timeline.documents.splice(index, 1);
        });
    }

    uploadedDocuments(files) {
        if (!this.timeline.documents) {
            this.timeline.documents = [];
        }
        files.file.forEach(file =>
            this.timelineService.addDocument(this.timeline.id, file.filename).subscribe(img => {
                const loading = this.timeline.documents.find(p => !p.id);
                if (loading) {
                    Object.assign(loading, img.data);
                } else {
                    this.timeline.documents.push(img.data);
                }
            })
        );
    }

    private getTimeline(slug) {
        this.reserve = slug === Routenames.newItem;
        const timeline$ = this.reserve
            ? this.timelineService.reserve()
            : this.timelineService.get(slug, this.route.snapshot.routeConfig?.path === this.Routenames.copy);
        timeline$.subscribe(
            timeline => {
                this.timeline = timeline.data;
                this.pageTitleService.setTitle(this.getPageTitleLabel(), this.timeline.title);
                this.generateForm(this.timeline);
            },
            error =>
                this.dialog
                    .confirm('Fout', error.message, 'Terug naar overzicht')
                    .then(() => this.backButtonService.navigateBack())
        );
    }

    private getPageTitleLabel(): string {
        return this.route.snapshot.routeConfig?.path === this.Routenames.copy
            ? 'Tijdlijn kopiëren'
            : 'Tijdlijn bewerken';
    }

    private generateForm(timeline: Timeline) {
        const images = timeline.images ? timeline.images.map(img => img.id) : [];
        if (!timeline.publish_date) {
            timeline.publish_date = new Date();
            if (timeline.publish_date.getMinutes() > 44) {
                timeline.publish_date.setHours(timeline.publish_date.getHours() + 2);
            } else {
                timeline.publish_date.setHours(timeline.publish_date.getHours() + 1);
            }
            timeline.publish_date.setMinutes(0);
        }
        const fcPublishTime = new FormControl(
            timeline.publish_date ? formatDate(timeline.publish_date, 'HH:mm', 'nl') : null,
            { validators: [validTime(), Validators.required], updateOn: 'blur' }
        );
        const fcPublishDate = new FormControl(timeline.publish_date ? timeline.publish_date : new Date());
        this.form = new FormGroup({
            id: new FormControl(timeline.id),
            title: new FormControl(timeline.title, Validators.required),
            intro: new FormControl(timeline.intro, Validators.required),
            alert: new FormControl(timeline.alert || false),
            adapt_image: new FormControl(timeline.adapt_image ?? true),
            content: new FormControl(timeline.content, Validators.required),
            publish_date: fcPublishDate,
            publish_time: fcPublishTime,
            images: new FormControl(images),
            image: new FormControl(timeline.image),
            customer_project_id: new FormControl(timeline.customer_project_id ?? this.projectId, Validators.required),
        });
        this.subscriptions.add(
            fcPublishTime.valueChanges.subscribe(value => handleTimeValueChange(value, fcPublishTime))
        );
        this.subscriptions.add(
            this.form.valueChanges.subscribe(() => {
                if (fcPublishTime.value && fcPublishDate.value) {
                    const time = fcPublishTime.value.split(':');
                    if (time.length === 2) {
                        const date = new Date(fcPublishDate.value);
                        date.setHours(+time[0]);
                        date.setMinutes(+time[1]);
                        if (date.toString() !== new Date(fcPublishDate.value).toString()) {
                            fcPublishDate.setValue(date);
                        }
                    }
                }
            })
        );
    }
}
